import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import App from './App'
import { AuthProvider } from './core/context/AuthContext'
import { ThemeProvider } from './core/context/ThemeContext'
import { TenantProvider } from './core/context/TenantContext'
import { TimezoneProvider } from './core/context/TimezoneContext'
import { PlatformProvider } from './core/context/PlatformContext'
import './index.css'

// On native platforms, prefix relative URLs with the server origin
// so fetch('/api/...') reaches the real backend instead of the device's localhost.
// Reads from localStorage on every call so it picks up changes immediately
// (e.g., after ServerSetup stores a new department URL).
if (Capacitor.isNativePlatform()) {
  const originalFetch = window.fetch
  window.fetch = function (input, init) {
    const serverUrl = (localStorage.getItem('scenelightServerUrl') || import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')
    if (serverUrl) {
      if (typeof input === 'string' && input.startsWith('/')) {
        input = serverUrl + input
      } else if (input instanceof Request && input.url.startsWith('/')) {
        input = new Request(serverUrl + input.url, input)
      }
    }
    return originalFetch.call(this, input, init)
  }
}

// On native, capture OAuth callback from deep links.
// Server-side flow sends: app.scenelight.portal://callback?token=JWT
// We store the JWT in localStorage so AuthContext picks it up on session check.
async function storeOAuthCallback(urlString) {
  try {
    const url = new URL(urlString)
    if (url.pathname === '/callback' || url.host === 'callback') {
      const token = url.searchParams.get('token')
      if (token) {
        localStorage.setItem('localAuthToken', token)
        try {
          const { Browser } = await import('@capacitor/browser')
          await Browser.close()
        } catch (e) {}
        window.dispatchEvent(new Event('mobileAuthReady'))
        return true
      }
    }
  } catch (e) {
    console.error('Deep link handling failed:', e)
  }
  return false
}

if (Capacitor.isNativePlatform()) {
  // Check launch URL (cold start from deep link)
  import('@capacitor/app').then(({ App: CapApp }) => {
    CapApp.getLaunchUrl().then((result) => {
      if (result?.url) storeOAuthCallback(result.url)
    }).catch(() => {})

    // Listen for deep links while app is running (warm resume)
    CapApp.addListener('appUrlOpen', (event) => {
      storeOAuthCallback(event.url)
    })
  }).catch(() => {})
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <PlatformProvider>
          <ThemeProvider>
            <TenantProvider>
              <TimezoneProvider>
                <App />
              </TimezoneProvider>
            </TenantProvider>
          </ThemeProvider>
        </PlatformProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
)

// Register service worker on web only (Capacitor bundles assets natively)
if (!Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      onNeedRefresh() {
        window.dispatchEvent(new Event('swUpdateReady'))
        window.__swUpdateReady = (accept) => {
          if (accept) updateSW(true)
        }
      },
      onOfflineReady() {
        console.log('App ready for offline use')
      }
    })
  })
}
